import { useState } from "react";
import { convertUserSelectionToScheduleOptions } from "../converters/converter"; // Adjust import
import { PlaydateInvite, ResponseType, useCreatePlaydateInviteResponseMutation, useUpdatePlaydateInviteResponseMutation } from "../../../gql/types"; // Adjust import
import { DayPartSelection, TimeSelectionRow } from "../../../components/time/types";
import { useToastContext } from "contexts/ToastContext";

type SubmitResponseOptions = {
    invite: PlaydateInvite;
    userSelection: DayPartSelection[] | TimeSelectionRow[];
    responseType: ResponseType;
    message?: string;
    isEditing?: boolean;
    onCompletion: (success: boolean, updatedInvite?: PlaydateInvite) => void;
};

export function useSubmitPlaydateResponse() {
    const [loading, setLoading] = useState(false);
    const [createResponse] = useCreatePlaydateInviteResponseMutation();
    const [updateResponse] = useUpdatePlaydateInviteResponseMutation();

    const { pushToastEvent } = useToastContext();


    const submitResponse = async ({
        invite,
        userSelection,
        responseType,
        message = "",
        isEditing = false,
        onCompletion,
    }: SubmitResponseOptions) => {
        setLoading(true);
        try {
            const input = {
                responseType,
                selectedScheduleOptions: convertUserSelectionToScheduleOptions(userSelection),
                message,
            };

            let response;
            if (isEditing && invite.viewerResponse?.responseID) {
                const { data } = await updateResponse({
                    variables: {
                        responseID: invite.viewerResponse.responseID,
                        input,
                    },
                });
                response = data?.updatePlaydateInviteResponse;
            } else {
                const { data } = await createResponse({
                    variables: {
                        input: {
                            inviteID: invite.inviteID,
                            ...input,
                        },
                    },
                });
                response = data?.createPlaydateInviteResponse;
            }

            if (response?.invite) {
                const updatedInvite = {
                    ...response.invite,
                    viewerResponse: response,
                } as PlaydateInvite;

                pushToastEvent({
                    eventType: "success",
                    message: "Your RSVP has been submitted.",
                });

                onCompletion(true, updatedInvite);
            } else {
                pushToastEvent({
                    eventType: "warning",
                    message: "Your RSVP has been submitted but data has not been updated.",
                });
                onCompletion(true);
            }
        } catch (err) {
            console.error(err);
            pushToastEvent({
                eventType: "error",
                message: "Submission failed.",
            });

            onCompletion(false);
        } finally {
            setLoading(false);
        }
    };

    return { submitResponse, loading };
}
